import { Box, Typography } from '@mui/material';
import {
  useGetTableDataForTimeRangeV2Query,
  useLazyGetTableDataForTimeRangeV2Query,
} from '../../../../services/mVPGraphQlService';
import {
  AutoCompleteController,
  Button,
  Table,
} from '../../../../components';
import { Column } from '../../../../types';
import { TFieldDetails2 } from '../../../../types/mvpCharts';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { MVPFormData } from '../../constants';
import { useAlertHelper } from '../../../../utils/functions/hooks';
import { RedirectNode } from '../../../../components/Table/actionNodes/redirectNode';
import * as yup from 'yup';
import { useFormWithSchema } from '../../../../utils/functions/yupFormTypescript';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import React from 'react';

type TProps = {
  fieldNumbers: number[] | undefined;
  startTime: number | string | undefined; // Allow undefined for safety
  endTime: number | string | undefined;
  commonFilters: MVPFormData;
  type: string;
  dataType: string;
  daysAfterPlantingValue: number;
  onNavigate?: (tabId: string) => void;
};

const formSchema = yup.object({
  type: yup.string().default('RAW_DATA_FIELDS_TYPE'),
});

export const MVPTable: React.FC<TProps> = ({
  startTime,
  endTime,
  commonFilters,
  type,
  dataType,
  daysAfterPlantingValue,
  onNavigate,
}) => {
  const defaultType =
    dataType == 'CUSTOM_DATE'
      ? 'RAW_DATA_FIELDS_TYPE'
      : 'RAW_DATA_FIELDS_WITH_DOP_TYPE';

  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormWithSchema(formSchema, {
    mode: 'onTouched',
    defaultValues: {
      type: defaultType,
    },
  });
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [tableData, setTableData] = useState<
    TFieldDetails2[]
  >([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [allTableData, setAllTableData] = useState<
    TFieldDetails2[]
  >([]);
  const { showError } = useAlertHelper();
  const wsId = useSelector(
    (state: RootState) => state.country.wsId
  );

  useEffect(() => {
    const newType =
      dataType == 'CUSTOM_DATE'
        ? 'RAW_DATA_FIELDS_TYPE'
        : 'RAW_DATA_FIELDS_WITH_DOP_TYPE';

    if (watch('type') !== newType) {
      setValue('type', newType);
    }
  }, [dataType]);

  const { data, isFetching, refetch } =
    useGetTableDataForTimeRangeV2Query(
      {
        commonFilters,
        wsId,
        startTime: startTime || 0,
        endTime: endTime || 0,
        tabContext: type,
        tableDataType: watch('type'),
        tablePageSize: pageSize,
        tablePageNumber: pageNo + 1,
        daysAfterPlanting: daysAfterPlantingValue,
      },
      {
        skip:
          watch('type') === 'RAW_DATA_FIELDS_TYPE' &&
          (!startTime || !endTime),
      }
    );

  useEffect(() => {
    if (startTime == undefined || endTime == undefined) {
      if (watch('type') === 'RAW_DATA_FIELDS_TYPE') {
        setTableData([]);
      } else {
        refetch();
        if (fieldDetails) {
          setTableData(fieldDetails);
          setTotalCount(
            data?.data?.GetTableDetailsV2?.totalCount || 0
          );
          setAllTableData(fieldDetails);
        }
      }
    }
  }, [watch('type')]);

  const columns: Column<
    TFieldDetails2,
    keyof TFieldDetails2
  >[] = [
    {
      key: 'fieldNumber',
      label: 'Field Number',
      cellModifier(data) {
        return (
          <Typography fontSize={13} fontWeight={600}>
            {data.fieldNumber}
          </Typography>
        );
      },
    },
    {
      key: 'fieldDetails',
      label: 'Region',
      cellModifier(data) {
        return data?.fieldDetails?.geographyData?.region;
      },
    },
    {
      key: 'fieldDetails',
      label: 'Village',
      cellModifier(data) {
        return data?.fieldDetails?.geographyData?.village;
      },
    },
    {
      key: 'fieldDetails',
      label: 'Registered Area',
      cellModifier(data) {
        return data.fieldDetails?.registeredArea?.toFixed(
          2
        );
      },
    },
    {
      key: 'fieldDetails',
      label: 'Geofence Area',
      cellModifier(data) {
        return data.fieldDetails.geofenceArea?.toFixed(2);
      },
    },
    {
      key: 'fieldDetails',
      label: 'Area Under reported',
      cellModifier(data) {
        return data.fieldDetails?.areaUnderReported?.toFixed(
          2
        );
      },
    },
    {
      key: 'fieldDetails',
      label: 'Grower Name',
      cellModifier(data) {
        return data.fieldDetails?.growerName;
      },
    },
    {
      key: 'fieldDetails',
      label: 'Organizer',
      cellModifier(data) {
        return data?.fieldDetails?.employeeData?.organizer;
      },
    },
    {
      key: 'fieldDetails',
      label: 'FA',
      cellModifier(data) {
        return data?.fieldDetails?.employeeData?.fa;
      },
    },
    {
      key: 'fieldDetails',
      label: 'Regional Manager',
      cellModifier(data) {
        return data?.fieldDetails?.employeeData
          ?.regionManager;
      },
    },
    {
      key: 'fieldDetails',
      label: 'Sowing Date',
      cellModifier(data) {
        return data?.fieldDetails?.sowingTime?.seconds > 0
          ? dayjs(
              data?.fieldDetails?.sowingTime?.seconds
                ? data?.fieldDetails?.sowingTime?.seconds *
                    1000
                : 0
            ).format('DD/MM/YYYY')
          : '-';
      },
    },
    {
      key: 'fieldDetails',
      label: 'Harvest Date',
      cellModifier(data) {
        return data?.fieldDetails?.harvestTime?.seconds == 0
          ? '-'
          : dayjs(
              data?.fieldDetails?.harvestTime?.seconds
                ? data?.fieldDetails?.harvestTime?.seconds *
                    1000
                : 0
            ).format('DD/MM/YYYY');
      },
    },
    {
      key: 'fieldDetails',
      label: 'Harvest Quantity',
      cellModifier(data) {
        return data?.fieldDetails?.harvestQuantity;
      },
    },
    {
      key: 'derivedFieldProps',
      label: 'Latest Index Date',
      cellModifier(data) {
        return data?.derivedFieldProps?.latestIndexTime
          ?.seconds
          ? dayjs(
              data?.derivedFieldProps?.latestIndexTime
                ?.seconds * 1000
            ).format('DD/MM/YYYY')
          : '-';
      },
    },
    {
      key: 'derivedFieldProps',
      label: 'Latest Index Value',
      cellModifier(data) {
        return data.latestIndex?.toString();
      },
    },
    {
      key: 'action',
      label: 'Action',
      cellModifier: (data: TFieldDetails2) => {
        return actionNode(data);
      },
    },
  ];

  const actionNode = (tableRowData: TFieldDetails2) => {
    const data = tableRowData;
    if (!data?.fieldNumber) {
      return null;
    }
    return (
      <div className="mt-4  ">
        <RedirectNode
          fieldNumber={data.fieldNumber}
          onNavigate={onNavigate}
        />
      </div>
    );
  };

  const fieldDetails =
    data?.data?.GetTableDetailsV2?.fieldDetails;

  useEffect(() => {
    if (fieldDetails) {
      setTableData(fieldDetails);
      setTotalCount(
        data?.data?.GetTableDetailsV2?.totalCount || 0
      );
      setAllTableData(fieldDetails);
    }
  }, [data]);

  const handlePageChange = (
    event: React.MouseEvent | null,
    newPageNumber: number
  ) => {
    setPageNo(newPageNumber);
    setTableData(
      getPaginatedData(
        newPageNumber,
        pageSize,
        allTableData
      )
    );
  };

  const getPaginatedData = (
    newPageNumber: number,
    itemsPerPage: number,
    allItemsList: TFieldDetails2[]
  ) => {
    const indexOfLastItem =
      (newPageNumber + 1) * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentPageItems = allItemsList?.slice(
      indexOfFirstItem,
      indexOfLastItem
    );
    return [...currentPageItems];
  };

  const [getTableDataIntoCSV] =
    useLazyGetTableDataForTimeRangeV2Query();

  const handleDownloadCsv = () => {
    getTableDataIntoCSV({
      commonFilters,
      startTime: startTime || 0,
      endTime: endTime || 0,
      tabContext: type,
      tableDataType: watch('type'),
      downloadCsv: true,
      tablePageSize: pageSize,
      tablePageNumber: pageNo + 1,
      daysAfterPlanting: daysAfterPlantingValue,
      wsId,
    })
      .unwrap()
      .then((response) => {
        const url =
          response?.data?.GetTableDetailsV2
            ?.csvPresignedUrl;
        window.open(url, '_blank');
      })
      .catch(() => {
        showError('Error while downloading file');
      });
  };

  const fieldOptions =
    dataType == 'CUSTOM_DATE'
      ? [
          {
            value: 'EXCLUDED_FIELDS_TYPE',
            label: 'Excluded Fields Table',
          },
          {
            value: 'RAW_DATA_FIELDS_TYPE',
            label: 'Graph data table',
          },
        ]
      : [
          {
            value: 'EXCLUDED_FIELDS_WITH_DOP_TYPE',
            label: 'Excluded Fields Table',
          },
          {
            value: 'RAW_DATA_FIELDS_WITH_DOP_TYPE',
            label: 'Graph data table',
          },
        ];
  return (
    <Box className="mt-16">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}>
        <div style={{ width: '250px' }}>
          <AutoCompleteController
            label="Select type"
            name="type"
            control={control}
            options={fieldOptions}
            error={errors.type?.message}
            disableClearable
          />
        </div>

        <Button
          variant="button--secondary"
          onClick={() => handleDownloadCsv()}
          disabled={tableData?.length > 0 ? false : true}>
          Download
        </Button>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}>
          <div
            style={{
              width: 16,
              height: 2,
              backgroundColor: '#FDAF20',
              marginRight: 8,
            }}
          />
          <span
            style={{ fontWeight: 500, fontSize: '16px' }}>
            PLD Fields
          </span>
        </div>
      </div>

      <Table
        columns={columns}
        data={tableData?.length ? tableData : []}
        rowsPerPage={pageSize}
        rowKey={'fieldNumber'}
        setRowsPerPage={setPageSize}
        count={totalCount}
        page={pageNo}
        setPage={setPageNo}
        isLoading={isFetching}
        rowsPerPageOptions={[pageSize]}
        pageChangeHandler={handlePageChange}
      />
    </Box>
  );
};
